import { FC, Fragment, useContext, useMemo } from 'react';

import {
  AmountBox,
  Button,
  ISelectOption,
  NumberInput,
  Search,
  Separator,
} from '@smartshell/ui-kit';
import { Control, Controller, useFieldArray } from 'react-hook-form';

import { LocalizationContext } from 'localization';
import { SettingsContext } from 'settings';
import { EComboItemType } from 'types/combo';

import { TFormValues, TNormalizedEntity } from '../interfaces';
import s from './EntityField.module.scss';

type TEntityFieldProps = {
  type: EComboItemType;
  control: Control<TFormValues>;
  options: Array<TNormalizedEntity>;
  normalizedEntities: Record<number, TNormalizedEntity>;
  disabled?: boolean;
};
export const EntityField: FC<TEntityFieldProps> = ({
  type,
  control,
  options,
  normalizedEntities,
  disabled,
}) => {
  const { t } = useContext(LocalizationContext);
  const {
    state: {
      settings: {
        currency: { sign: currency },
      },
    },
  } = useContext(SettingsContext);

  const key = type === EComboItemType.GOOD ? 'goods' : 'services';
  const placeholder =
    type === EComboItemType.GOOD
      ? t('combo.goods.add')
      : t('combo.services.add');

  const { fields, append, remove } = useFieldArray<TFormValues, typeof key>({
    control,
    name: key,
  });

  const SELECT_OPTIONS = useMemo(
    () =>
      options
        .filter(
          (option) => !fields.some((field) => field.entity_id === option.id)
        )
        .map<ISelectOption>((option) => ({
          value: option.id,
          title: option.title,
        })),
    [fields.length, options]
  );

  return (
    <div className={s.container}>
      {fields.map((item, itemIndex) => {
        return (
          <Fragment key={item.id}>
            <div className={s.field}>
              <div className={s.entity}>
                <div className={s.data}>
                  <span className={s.title}>
                    {normalizedEntities[item.entity_id]?.title}
                  </span>
                  <span className={s.cost}>
                    {normalizedEntities[item.entity_id]?.cost} {currency}
                  </span>
                </div>

                <Controller
                  control={control}
                  render={({ field }) => {
                    return <AmountBox {...field} />;
                  }}
                  name={`${key}.${itemIndex}.amount`}
                />
                <Button
                  iconName="CloseIcon"
                  color="red"
                  variant="ghost"
                  size="s"
                  onlyIcon
                  onClick={() => remove(itemIndex)}
                  className={s.remove}
                />
              </div>
              <Controller
                control={control}
                render={({ field, fieldState: { error } }) => {
                  return (
                    <NumberInput
                      title={t('combo.item.price')}
                      {...field}
                      rightContent={
                        <span className={s.currency}>{`${currency} / ${t(
                          'pcs'
                        )}.`}</span>
                      }
                      className={s.new__price}
                      error={error?.message}
                      bordered
                    />
                  );
                }}
                name={`${key}.${itemIndex}.price`}
                rules={{
                  min: {
                    value: 1,
                    message: t('errors.min', 1, currency),
                  },
                  max: {
                    value: 16000000,
                    message: t('errors.max', '16 000 000', currency),
                  },
                  required: t('errors.required'),
                }}
              />
            </div>
            <Separator />
          </Fragment>
        );
      })}
      <Search
        placeholder={placeholder}
        name={key}
        items={SELECT_OPTIONS}
        disabled={disabled}
        value={null}
        variant="secondary"
        notFoundText={
          key === 'services' ? t('service.notFound') : t('goods.notFound')
        }
        onChange={(value) => {
          const entity = options.find((option) => option.id === value);

          if (!entity) return;

          append({
            entity_id: entity.id,
            entity_type: type,
            price: entity.cost,
            amount: 1,
          });
        }}
        bordered
      />
    </div>
  );
};
